import { useState } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import isEqual from 'lodash/isEqual';

import * as K from 'common/constants';
import { FacetsCompletionInfo } from 'common/types';
import { FacetData, QueryType } from 'common/graphqlTypes';
import { generateQueryTimeRange } from 'common/util/time';
import { getUrlParam } from 'common/util/location';
import { getRequestIds } from 'common/util/lrqRequest';
import { updateFacetCompletionInfo } from 'common/util/facets';
import { facetInfoFromDataState, facetInfoNeededState } from 'components/search/facets.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import {
  facetValuesRequestsState,
  initialLogRequestsState,
  plotDataRequestsState,
} from 'components/search/SearchLogsPage/SearchStateManager.state';
import { dashboardFacetValuesRequestsState } from 'components/dashboards/dashboards.state';
import { EventQueryInput, generateTopFacetsRequest, LRQRequest } from 'hooks/useLRQ/useLRQ';
import { EventCacheData, useEventCache } from 'hooks/useLRQ/useEventCache';
import LRQRequestManager from 'components/common/Managers/LRQRequestManager';


const START_TIME = '15m';

/**
 * This component manages globally available facet information, which is needed by many components across the app. The
 * information is kept in Recoil state and has two forms:
 *
 *   1. The Recoil atom 'facetInfoState' holds data that can be used for autocmplete, among other things. It is a map
 *      with facet names as keys. The values are objects with booleans for whether the facet is numeric or boolean, and
 *      a list of known values for that facet.
 *   2. A Recoil selector 'facetListState' which is a list of all known facet names (for the session).
 *
 * This manager has three jobs:
 *
 *   1. If there is no facet information available and a component has indicated that it needs it, make a TOP_FACETS
 *      request to get some facet data. A component indicates that it needs facet info by setting the global
 *      'facetInfoNeeded' flag in Recoil state. The first time that becomes true, the request is made. Components
 *      should make sure that the request isn't made unnecessarily, for example before a search that also returns
 *      facets.
 *   2. When a TOP_FACETS response has been received, fold the data into the global facet info.
 *   3. When a FACET_VALUES response has been received, fold the data into the global facet info.
 *
 * This component renders an LRQRequest if a TOP_FACETS request needs to be made.
 */
function FacetsManager(): JSX.Element {

  const facetInfoNeeded = useRecoilValue(facetInfoNeededState);
  const facetInfo = useRecoilValue(facetInfoFromDataState);
  const isFacetInfoLoaded = Object.keys(facetInfo).length > 0;
  const [ facetsRequest, setFacetsRequest ] = useState<LRQRequest | null>(null);

  const timezone = useRecoilValue(timezoneState);
  const queryTimeRange = generateQueryTimeRange(timezone, START_TIME);
  const { startTime, endTime } = queryTimeRange;
  const topFacetsRequest = generateTopFacetsRequest({ startTime, endTime } as EventQueryInput);

  // If we don't already have cached facet fields, set the request for LRQRequestManager
  const prevFacetInfoNeeded = usePrevious(facetInfoNeeded);
  useConditionalEffect(() => {
    setFacetsRequest(topFacetsRequest as LRQRequest);
  }, !isFacetInfoLoaded && facetInfoNeeded && !prevFacetInfoNeeded);

  // look for facet data in TOP_FACETS responses
  const initialLogRequests = useRecoilValue(initialLogRequestsState);
  const plotDataRequests = useRecoilValue(plotDataRequestsState);
  const mode = getUrlParam(window.location.search, K.URL_PARAM.MODE);
  let searchRequests: LRQRequest[] = [];
  if (mode === K.EVENT_MODE.GRAPH) {
    searchRequests = plotDataRequests;
  } else if (!mode || mode === K.EVENT_MODE.LOG) {
    searchRequests = initialLogRequests;
  }
  const facetRequestIds = getRequestIds(searchRequests.concat(facetsRequest as LRQRequest), QueryType.TOP_FACETS);
  const { results: cachedFacetResults } = useEventCache(facetRequestIds);

  // look for facet data in FACET_VALUES responses
  const facetValuesRequests = useRecoilValue(facetValuesRequestsState);
  const dashboardFacetValuesRequests = useRecoilValue(dashboardFacetValuesRequestsState);
  const facetValuesDataIds = getRequestIds(facetValuesRequests.concat(dashboardFacetValuesRequests), QueryType.FACET_VALUES);
  const { results: cachedFacetValuesResults } = useEventCache(facetValuesDataIds);

  // update facet info when we find a FacetQuery response
  const updateFacetInfo = useRecoilCallback(({ snapshot,  set }) => (newFacetDatas: FacetData[]): void => {
    const facetInfo = snapshot.getLoadable(facetInfoFromDataState).contents as FacetsCompletionInfo;
    set(facetInfoFromDataState, updateFacetCompletionInfo(facetInfo, newFacetDatas));
  });

  // If we got results from a TOP_FACETS query, set them in state and cache them for later use
  const facetResults = cachedFacetResults.find((result: EventCacheData) => result.isDone);
  const prevFacetResults = usePrevious(facetResults);
  useConditionalEffect(() => {
    const facetData = facetResults?.facetData;
    if (facetData?.facets?.length) {
      updateFacetInfo([facetData]);
    }
  }, facetResults && !isEqual(facetResults, prevFacetResults));

  // If we got results from a FACET_VALUES query, set them in state and cache them for later use
  const facetValuesResults = cachedFacetValuesResults.filter((result: EventCacheData) => result.isDone);
  const prevFacetValuesResults = usePrevious(facetValuesResults);
  useConditionalEffect(() => {
    const facetDatas = facetValuesResults.filter((facetValuesResults) => facetValuesResults.facetData?.facets?.length > 0)
      .map((facetValuesResults) => facetValuesResults.facetData);
    if (facetDatas.length > 0) {
      updateFacetInfo(facetDatas);
    }
  }, facetValuesResults && !isEqual(facetValuesResults, prevFacetValuesResults));

  return <>{facetsRequest && <LRQRequestManager key={facetsRequest.key} request={facetsRequest} />}</>;
}

export default FacetsManager;
